import {
  RECENT_WINDOW_DAYS,
  adherenceFor,
  buildSeries,
  describeRecentAverage,
  isQuantitative,
  questionFor,
  recentAverage,
  consistency,
  resolveRange,
  type CheckInTemplate,
  type ConsistencyDay,
  type Entry,
  type Timestamp,
  type Tag,
  type Track,
  type ValidationError,
  readPreferences,
  type PaletteColour,
} from '@anytracker/core'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { checkInColour, hueStyle } from '../../charts/colours.js'
import { Button } from '../../components/Button.js'
import { ActionButton, ICONS, Icon } from '../../components/Layout.js'
import { EmptyState } from '../../components/EmptyState.js'
import { NavigationFrame } from '../../shell/NavigationFrame.js'
import { useToast } from '../../components/Toast.js'
import { ValueInput } from '../../components/value-input/ValueInput.js'
import { useDeclareUnsaved } from '../../pwa/UnsavedWork.js'
import { useServices } from '../../services/ServicesProvider.js'
import { TagPicker } from './TagPicker.js'
import { SavedConfirmation } from './SavedConfirmation.js'
import { TimeSheet, timeChipLabel } from './TimeSheet.js'
import { prune, useDraft, type DraftState } from './useDraft.js'
import styles from './LogFlow.module.css'

/** How many days the confirmation's streak strip shows. */
const STRIP_DAYS = 14

/**
 * Recording a measurement, one question at a time (T-805).
 *
 * The most important screen in the application: everything else operates on
 * what this captures, and adherence is the product requirement. **This replaces
 * the single form rather than joining it** — two paths to the same result would
 * drift, and one of them would be the one nobody tested.
 *
 * The argument for the change is that a form of eight controls is a wall, and a
 * wall is what stops someone logging on the third morning. A gap in the record
 * is indistinguishable from a day nothing happened (ADR-0005), so the cost of
 * friction here is measured in wrong conclusions later.
 *
 * Two rules run through the whole file:
 *
 *   - **Skipping means absent, never zero.** A skipped track records no
 *     measurement at all. A missing reading and a reading of zero are entirely
 *     different claims, and only one of them is true.
 *   - **The flow never branches on track kind.** `ValueInput` dispatches, so a
 *     fourth value type is a change in one place rather than here.
 */
export function LogFlowScreen() {
  const { repos, clock, storage } = useServices()
  const { show } = useToast()
  const navigate = useNavigate()
  const params = useParams()
  const templateId = params['templateId'] ?? null

  const [template, setTemplate] = useState<CheckInTemplate | null>(null)
  const [tracks, setTracks] = useState<Track[]>([])
  const [missing, setMissing] = useState<string[]>([])
  const [tags, setTags] = useState<Tag[]>([])
  const [recent, setRecent] = useState<Entry[]>([])
  const [loaded, setLoaded] = useState(false)
  const [errors, setErrors] = useState<readonly ValidationError[]>([])
  const [step, setStep] = useState(0)
  /*
   * `null` until read, so the flow never renders with the wrong number of
   * steps and then re-renders. A progress counter that says "1 / 3" and
   * silently becomes "1 / 4" is the sort of thing people stop trusting.
   */
  const [finishStep, setFinishStep] = useState<boolean | null>(null)
  const [timeOpen, setTimeOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  /*
   * What was just recorded, or `null` while still recording.
   *
   * Held here rather than given a route of its own. The data is in hand at this
   * moment and nowhere else — a `/log/done` URL would show an empty screen to
   * anyone who refreshed it, which is a worse answer than not being linkable.
   */
  const [saved, setSaved] = useState<{
    readonly count: number
    readonly at: Timestamp
    readonly trackId: string | null
    readonly streak: number
    readonly days: readonly ConsistencyDay[]
  } | null>(null)

  const initial = useCallback(
    (): DraftState => ({
      at: clock.now(),
      tagIds: [],
      note: '',
      values: {},
      slotNotes: {},
    }),
    [clock],
  )

  const { draft, update, setValue, clear, restored } = useDraft(
    storage,
    templateId,
    initial,
  )

  const load = useCallback(async () => {
    /*
     * The window the average is drawn from, fetched by range rather than by
     * reading everything. A user with two years of history has thousands of
     * entries and this screen needs a fortnight of them.
     */
    const now = clock.now()
    const [allTracks, allTags, window, preferences] = await Promise.all([
      repos.tracks.list({ includeArchived: true }),
      repos.tags.list(),
      repos.entries.listByRange({
        fromEpochMs: now.epochMs - RECENT_WINDOW_DAYS * 86_400_000,
        toEpochMs: now.epochMs,
      }),
      readPreferences(storage),
    ])
    setFinishStep(preferences.finishStep)
    setTags(allTags)
    setRecent(window)

    if (templateId === null) {
      // Ad hoc: every active track, in the user's own order. That order is now
      // the script for a conversation rather than a list-sorting detail.
      setTracks(allTracks.filter((track) => !track.archived))
      setLoaded(true)
      return
    }

    const found = await repos.templates.get(templateId)
    if (!found) {
      setLoaded(true)
      return
    }
    setTemplate(found)

    const byId = new Map(allTracks.map((track) => [track.id, track]))
    const resolved: Track[] = []
    const absent: string[] = []
    // Template order, not track order — the user arranged these deliberately.
    for (const trackId of found.trackIds) {
      const track = byId.get(trackId)
      if (track === undefined) {
        absent.push(trackId)
      } else if (!track.archived) {
        // An archived track is dropped rather than asked about: it cannot be
        // logged against usefully, and a question that exists only to be
        // dismissed is worse than one that is not there.
        resolved.push(track)
      }
    }
    setTracks(resolved)
    setMissing(absent)
    setLoaded(true)
  }, [repos, clock, storage, templateId])

  useEffect(() => {
    void load()
  }, [load])

  // The template's default tags, applied once when the flow opens fresh.
  useEffect(() => {
    if (template && draft && !restored && draft.tagIds.length === 0) {
      update({ tagIds: [...template.defaultTagIds] })
    }
    // Only when the template first resolves; re-running would fight the user
    // deselecting a default tag.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, restored])

  const filled = useMemo(
    () => (draft ? Object.keys(prune(draft.values, tracks)).length : 0),
    [draft, tracks],
  )

  /*
   * Unsaved for the flow's whole lifetime, not only once something is filled.
   *
   * T-204 defers the service worker's update prompt while an editor has unsaved
   * work. A form was a few seconds; this is eight screens and possibly a
   * minute, and a reload takes the lot with no draft written yet at step one.
   * The condition is the flow being open, because that is the thing that must
   * not be interrupted.
   */
  useDeclareUnsaved(tracks.length > 0)

  /*
   * The finish step is a step, so it counts (T-1101).
   *
   * Tags and a note used to hang off the bottom of the last question's screen,
   * below the control and below the fold on a phone — which is a fair
   * description of "not there". Worse, there was no note field in the flow at
   * all: the only way to write one was to find the entry on the timeline
   * afterwards and open it, by which point the context you would have written
   * down is the context you have forgotten.
   */
  const questions = tracks.length
  const showFinish = finishStep === true && questions > 0
  const total = questions + (showFinish ? 1 : 0)
  const onFinish = showFinish && step >= questions
  const current = onFinish
    ? undefined
    : tracks[Math.min(step, Math.max(questions - 1, 0))]
  /** The last *question*, which is not the last step once the finish step is on. */
  const onLast = step >= questions - 1

  /*
   * "Is this normal for me?", answered without leaving the flow (T-807).
   *
   * Computed in core and worded there too — the window, the floor below which
   * it says nothing, and the sentence itself. A second screen wording the same
   * fact differently is how two views of one number end up disagreeing.
   */
  const average = useMemo(() => {
    if (!current || !isQuantitative(current.kind)) {
      return null
    }
    const series = buildSeries(current, recent)
    if (series.kind !== 'quantitative') {
      return null
    }
    const found = recentAverage(series, clock.now())
    return found === null ? null : describeRecentAverage(current, found)
  }, [current, recent, clock])

  function leave() {
    void navigate('/')
  }

  async function createTag(name: string) {
    const result = await repos.tags.create(name)
    if (!result.ok) {
      show({
        message: result.errors[0]?.message ?? 'Could not add that.',
        tone: 'danger',
      })
      return
    }
    setTags(await repos.tags.list())
    if (draft) {
      update({ tagIds: [...draft.tagIds, result.value.id] })
    }
  }

  async function save() {
    if (!draft || saving) {
      return
    }
    setErrors([])
    setSaving(true)

    const measurements = tracks
      .filter((track) => draft.values[track.id] !== undefined)
      .map((track) => {
        /*
         * Per-measurement notes are carried, not authored.
         *
         * The redesign has no per-question note and this flow offers no way to
         * write one. A draft started in the old form still can, though, and
         * dropping the field here would silently delete what someone had
         * already typed before the switch. Also in `docs/design-issues.md`.
         */
        const note = draft.slotNotes[track.id]?.trim()
        return {
          trackId: track.id,
          value: draft.values[track.id]!,
          ...(note === undefined || note === '' ? {} : { note }),
        }
      })

    // Stamped once, from the header's own clock reading, rather than restamped
    // per step. Eight screens take a minute; eight timestamps a minute apart
    // would turn one check-in into a smear across the timeline.
    const result = await repos.entries.create({
      at: draft.at,
      tagIds: draft.tagIds,
      measurements,
      ...(templateId === null ? {} : { templateId }),
      ...(draft.note.trim() === '' ? {} : { note: draft.note.trim() }),
    })

    setSaving(false)
    if (!result.ok) {
      // Every failure at once, not one at a time — core reports them together
      // and re-submitting to discover the next one is its own kind of friction.
      setErrors(result.errors)
      return
    }

    await clear()

    /*
     * The confirmation, not a toast and not a navigation (T-809).
     *
     * This is the only place the app connects logging to the reason for
     * logging — "See it on the chart" takes someone from the thing they just
     * typed to the thing it is for. The streak is computed here because the
     * entry has only just landed; reading it before the write would be one
     * short.
     */
    const range = resolveRange({ kind: 'preset', preset: 'last30' }, { clock })
    const window = await repos.entries.listByRange(range)
    setSaved({
      count: measurements.length,
      at: draft.at,
      trackId: measurements[0]?.trackId ?? null,
      /*
       * The streak over thirty days, the strip over the last fourteen.
       *
       * A run longer than the strip reads as its true length — "that's 23 days
       * without a gap" beside fourteen lit dots — rather than being clipped to
       * whatever happens to fit on screen.
       */
      streak: adherenceFor(window, range).trailingStreakDays,
      days: consistency(window, STRIP_DAYS, clock.now(), clock.timeZone()).days,
    })
  }

  /*
   * Skipping records nothing and moves on.
   *
   * On the last step "moving on" means finishing, not sitting still: leaving
   * the button doing nothing at all reads as broken rather than as "you have
   * reached the end". And skipping every track leaves no measurements — an
   * entry with none is not a record of anything, so there is nothing to write
   * and the flow says so rather than saving an empty shell.
   */
  function skip() {
    if (!current) {
      return
    }
    setValue(current.id, undefined)
    if (!onLast) {
      setStep(step + 1)
      return
    }
    const remaining = tracks.filter(
      (track) => track.id !== current.id && draft?.values[track.id] !== undefined,
    ).length
    /*
     * Skipping the last question still goes to the finish step, as long as
     * something was recorded. Jumping straight to saving would take the tags
     * and the note away from anyone whose last answer happened to be a skip.
     */
    if (showFinish && remaining > 0) {
      setStep(step + 1)
      return
    }
    if (remaining === 0) {
      void clear().then(() => {
        show({ message: 'Nothing recorded — every question was skipped.' })
        leave()
      })
      return
    }
    void save()
  }

  function back() {
    if (step === 0) {
      // Back from the first question leaves the flow. There is nothing behind
      // it, and a disabled back button on the opening screen is a dead end.
      leave()
      return
    }
    setStep(step - 1)
  }

  /*
   * The confirmation replaces the flow rather than covering it.
   *
   * Nothing behind it is still useful — the entry is written and the draft is
   * cleared — and rendering it over a live flow would leave a half-torn-down
   * screen underneath for anyone who scrolled.
   */
  if (saved !== null) {
    return (
      <SavedConfirmation
        checkInName={template?.name ?? 'Entry'}
        count={saved.count}
        at={saved.at}
        streak={saved.streak}
        days={saved.days}
        onSeeChart={
          // Only when there is a track to chart. Every saved entry has at least
          // one measurement — the flow refuses to write an empty one — so this
          // is a type-level nicety rather than a case anyone will meet.
          saved.trackId === null
            ? null
            : () => void navigate(`/insights?track=${saved.trackId ?? ''}`)
        }
        onHome={() => {
          show({ message: 'Recorded.' })
          leave()
        }}
      />
    )
  }

  if (!loaded || !draft) {
    return (
      <div className={styles['flow']}>
        <h1 className={styles['plainTitle']} data-bleed>
          Log
        </h1>
        <p>Loading…</p>
      </div>
    )
  }

  /*
   * The one state in this flow that keeps the navigation.
   *
   * The flow is outside the app shell because a tab bar under a half-answered
   * check-in is four standing offers to abandon it (T-805). That reasoning is
   * about a flow in progress, and here there is no flow: nothing has been
   * entered, so there is nothing to abandon and no cost to a way out.
   *
   * Without it this screen is a dead end with exactly one exit, and in an
   * installed PWA there is no browser back button behind it — somebody who taps
   * Log before making a track is stuck on a page telling them to go somewhere
   * they cannot get to. Verified as zero links and zero navigation landmarks
   * before this was added.
   */
  if (total === 0) {
    return (
      <NavigationFrame>
        <h1 className={styles['plainTitle']} data-bleed>
          Log
        </h1>
        <EmptyState
          title="Nothing to ask about yet"
          body={
            templateId === null
              ? 'Set up a track and this becomes a question you can answer.'
              : 'Every track in this check-in has been archived or removed.'
          }
          action={
            <Button onClick={() => void navigate('/tracks')}>Set up a track</Button>
          }
        />
      </NavigationFrame>
    )
  }

  const checkInName = template?.name ?? 'Log anything'
  const value = current ? draft.values[current.id] : undefined
  /*
   * The whole flow wears the check-in's colour, so the screen says which
   * check-in this is before a word is read. "Log anything" has no check-in and
   * wears the accent.
   */
  const colour: PaletteColour = template === null ? 'leaf' : checkInColour(template)
  const saveLabel =
    filled === 0
      ? 'Nothing to save yet'
      : `Save ${filled === 1 ? '1 measurement' : `${String(filled)} measurements`}`

  const errorBox =
    errors.length > 0 ? (
      <div className={styles['errors']} role="alert">
        {errors.map((error) => (
          <span key={error.code} className={styles['error']}>
            {error.message}
          </span>
        ))}
      </div>
    ) : null

  return (
    <div className={`${styles['flow']} rise`} style={hueStyle(colour)}>
      <header className={styles['header']}>
        <button
          type="button"
          className={styles['back']}
          aria-label={step === 0 ? 'Leave without recording' : 'Previous question'}
          onClick={back}
        >
          <Icon path={step === 0 ? ICONS.close : ICONS.chevronLeft} />
        </button>

        {/*
          Decoration with the same information in words beside it. The bars
          are a shape; "2 of 4" is the answer to "how much is left", and a
          progress indicator that only exists as colour is not an answer for
          anyone who cannot see it.
        */}
        <div className={styles['bars']} aria-hidden="true">
          {/* One per step, so the finish step gets a bar. Keyed by index
              rather than by track, because the last one is not a track. */}
          {Array.from({ length: total }, (_, index) => (
            <span
              key={index}
              className={`${styles['bar']} ${index <= step ? styles['barDone'] : ''}`}
            />
          ))}
        </div>
        <span className={styles['counter']}>
          {String(step + 1)} of {String(total)}
        </span>
      </header>

      {current && (
        <>
          <div className={styles['prompt']}>
            <p className={styles['eyebrow']}>{checkInName}</p>
            {/*
              The question is the page heading, because each step is its own
              page as far as anyone navigating by heading is concerned.

              `questionFor` is core's, not this screen's: a track's question is
              either its own or derived from its name, and the native app and
              the report have to give the same answer (T-810).
            */}
            <h1 className={styles['question']} id="log-flow-question">
              {questionFor(current)}
            </h1>
            <p className={styles['description']}>{current.description ?? current.name}</p>
            <Notices restored={restored && filled > 0} missing={missing.length} />
          </div>

          <div className={styles['body']}>
            <div className={styles['control']}>
              {/* Polymorphic: this flow never asks what kind of track it is. */}
              <ValueInput
                track={current}
                value={value ?? null}
                variant="full"
                labelledBy="log-flow-question"
                onChange={(next) => setValue(current.id, next ?? undefined)}
              />

              {average !== null && (
                <p className={styles['average']}>
                  <Icon path="M4 18V6m0 12h16M8 14l4-5 3 3 4-6" />
                  {average}
                </p>
              )}

              {/*
                Tags on the last step only, and only when the finish step is
                off — with it on this is the same control twice, and two places
                to set one thing is how they end up disagreeing on screen.

                Still `TagPicker`, which carries "+ New tag": this is one of the
                places a tag can be created, and dropping that to match a
                drawing would leave someone able to delete their last tag and
                never make another (`docs/design-issues.md`).
              */}
              {onLast && !showFinish && (
                <div className={styles['tags']}>
                  <p className={styles['tagsLabel']}>Tags</p>
                  <TagPicker
                    tags={tags}
                    selected={draft.tagIds}
                    onToggle={(tagId) =>
                      update({
                        tagIds: draft.tagIds.includes(tagId)
                          ? draft.tagIds.filter((id) => id !== tagId)
                          : [...draft.tagIds, tagId],
                      })
                    }
                    onCreate={createTag}
                  />
                </div>
              )}
            </div>

            {errorBox}

            <TimeRow
              label={timeChipLabel(draft.at, clock.now())}
              onOpen={() => setTimeOpen(true)}
            />
            <button
              type="button"
              className={styles['discard']}
              onClick={() => {
                void clear().then(leave)
              }}
            >
              Discard
            </button>
          </div>

          {/*
            Skip and the main action, side by side under the thumb. Skip stays
            a full-size target: a skip that is hard to press is a nudge towards
            answering anyway, and an invented reading is worse than a gap
            because nothing downstream can tell it apart from a real one.
          */}
          <footer className={styles['footer']}>
            <ActionButton onClick={skip}>Skip</ActionButton>
            {onLast && !showFinish ? (
              <ActionButton
                primary
                disabled={filled === 0 || saving}
                onClick={() => void save()}
              >
                {saveLabel}
              </ActionButton>
            ) : (
              <ActionButton
                primary
                {...(onLast && showFinish && filled === 0
                  ? {}
                  : { icon: ICONS.arrowRight })}
                // On the last question with the finish step on, "Next" leads to
                // it — and it is disabled with nothing recorded for the same
                // reason "Save" is: there would be nothing to attach a tag to.
                disabled={onLast && showFinish && filled === 0}
                onClick={() => setStep(step + 1)}
              >
                {onLast && showFinish && filled === 0 ? 'Nothing to save yet' : 'Next'}
              </ActionButton>
            )}
          </footer>
        </>
      )}

      {/*
        The finish step (T-1101).

        Its own screen, because the two things on it are the two things people
        forget. Tags used to sit under the last question's control — below the
        fold on a phone, after the answer, next to a primary button — and a note
        could not be written in the flow at all. Both are only cheap to record
        at the moment of recording; a week later they are a guess.

        Skippable in one tap and never required. An entry with no tags and no
        note is a complete entry, and a flow that insists otherwise is one
        people abandon halfway.
      */}
      {onFinish && (
        <>
          <div className={styles['prompt']}>
            <p className={styles['eyebrow']}>{checkInName}</p>
            <h1 className={styles['question']}>Anything to add?</h1>
            <p className={styles['description']}>
              Optional, and easier now than from memory later.
            </p>
          </div>

          <div className={styles['body']}>
            <div className={styles['finish']}>
              <div className={styles['tags']}>
                <p className={styles['tagsLabel']}>Tags</p>
                <TagPicker
                  tags={tags}
                  selected={draft.tagIds}
                  onToggle={(tagId) =>
                    update({
                      tagIds: draft.tagIds.includes(tagId)
                        ? draft.tagIds.filter((id) => id !== tagId)
                        : [...draft.tagIds, tagId],
                    })
                  }
                  onCreate={createTag}
                />
              </div>

              <div className={styles['noteField']}>
                <label className={styles['tagsLabel']} htmlFor="log-flow-note">
                  Note
                </label>
                <textarea
                  id="log-flow-note"
                  className={styles['note']}
                  rows={4}
                  placeholder="What was going on?"
                  value={draft.note}
                  onChange={(event) => update({ note: event.target.value })}
                />
              </div>
            </div>

            {errorBox}

            <TimeRow
              label={timeChipLabel(draft.at, clock.now())}
              onOpen={() => setTimeOpen(true)}
            />
          </div>

          <footer className={styles['footer']}>
            <ActionButton
              onClick={() => {
                update({ tagIds: [], note: '' })
                void save()
              }}
            >
              Save without these
            </ActionButton>
            <ActionButton
              primary
              disabled={filled === 0 || saving}
              onClick={() => void save()}
            >
              {saveLabel}
            </ActionButton>
          </footer>
        </>
      )}

      <TimeSheet
        open={timeOpen}
        at={draft.at}
        now={clock.now()}
        onChange={(at) => update({ at })}
        onClose={() => setTimeOpen(false)}
      />
    </div>
  )
}

/**
 * When the entry will be stamped, and the way to change it.
 *
 * One button for the whole row, so its name starts with the time it reads —
 * "Now · 12:48" — and "Change time" says what tapping does.
 */
function TimeRow({
  label,
  onOpen,
}: {
  readonly label: string
  readonly onOpen: () => void
}) {
  return (
    <button type="button" className={styles['timeRow']} onClick={onOpen}>
      <span className={styles['timeWhen']}>
        <Icon path={ICONS.clock} />
        {label}
      </span>
      <span className={styles['timeChange']}>Change time</span>
    </button>
  )
}

/** Things worth knowing before answering: a restored draft, a missing track. */
function Notices({
  restored,
  missing,
}: {
  readonly restored: boolean
  readonly missing: number
}) {
  return (
    <>
      {restored && (
        <p className={styles['notice']}>
          Picking up where you left off — nothing was lost.
        </p>
      )}
      {missing > 0 && (
        <p className={styles['notice']}>
          {missing === 1
            ? 'One track in this check-in no longer exists'
            : `${String(missing)} tracks in this check-in no longer exist`}{' '}
          and cannot be asked for. The rest still work.
        </p>
      )}
    </>
  )
}

/** A check-in's colour as the custom properties the flow's tinted areas read. */
